import {
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  Flex,
  FormLabel,
  Switch,
  Text,
} from "@chakra-ui/react";
import * as DateFns from "date-fns";
import { FieldArray, useFormikContext } from "formik";
import { InputControl, SelectControl } from "formik-chakra-ui";
import React from "react";
import { DEFAULT_DATE_TYPE_FORMAT } from "src/constants/date";
import { DateWithoutTime } from "src/schemas/date";
import * as AF from "src/types/formTemplate";
import { z } from "zod";

type GradeConfig = { id: string; label: string };

const BirthdateAfterFilter = z.object({
  type: z.literal("BirthdateAfterFilter"),
  gradeIds: z.array(z.string()),
  startDate: DateWithoutTime,
  endDate: z.string().optional(),
});
const BirthdateBeforeFilter = z.object({
  type: z.literal("BirthdateBeforeFilter"),
  gradeIds: z.array(z.string()),
  startDate: z.string().optional(),
  endDate: DateWithoutTime,
});
const BirthdateBetweenFilter = z.object({
  type: z.literal("BirthdateBetweenFilter"),
  gradeIds: z.array(z.string()),
  startDate: DateWithoutTime,
  endDate: DateWithoutTime,
});

const FilterSchema = z.discriminatedUnion("type", [
  BirthdateAfterFilter,
  BirthdateBeforeFilter,
  BirthdateBetweenFilter,
]);

type Filter = z.infer<typeof FilterSchema>;
export const FormikSchema = z.object({
  birthdateFilter: z.discriminatedUnion("type", [
    z.object({
      type: z.literal("on"),
      filters: z
        .array(FilterSchema)
        .min(1, "At least 1 date range is required"),
    }),
    z.object({
      type: z.literal("off"),
      filters: z.array(FilterSchema),
    }),
  ]),
});
export type FormikType = z.infer<typeof FormikSchema>;

type Props = {
  questionId: uuid;
  disabled?: boolean;
  gradesConfig: GradeConfig[];
};
export const ConfigureDateOfBirth: React.FC<Props> = ({
  questionId,
  disabled,
  gradesConfig,
}) => {
  const today = DateFns.formatDate(Date.now(), DEFAULT_DATE_TYPE_FORMAT);
  const formik = useFormikContext<FormikType>();

  const birthdateFilters = React.useMemo(() => {
    return formik.values.birthdateFilter.type === "on"
      ? formik.values.birthdateFilter.filters ?? []
      : [];
  }, [formik.values]);

  const getFilterByGradeId = React.useCallback(
    (gradeId: uuid) => {
      return birthdateFilters.filter((filter) =>
        filter.gradeIds.includes(gradeId)
      );
    },
    [birthdateFilters]
  );

  const filteredGradesWithoutGradeConfig = React.useMemo(() => {
    const noGradeConfig: string[] = [];

    birthdateFilters.forEach((filter) => {
      filter.gradeIds.forEach((gradeId) => {
        if (!gradesConfig.find((gradeConfig) => gradeConfig.id === gradeId)) {
          noGradeConfig.push(gradeId);
        }
      });
    });

    return noGradeConfig;
  }, [gradesConfig, birthdateFilters]);

  const handleCheckboxChange = React.useCallback(
    async (isChecked: boolean, gradeConfigId: string, filterIndex: number) => {
      const gradeIds =
        formik.values.birthdateFilter.filters?.[filterIndex]?.gradeIds;
      const nextGradeIds = isChecked
        ? gradeIds?.filter((gradeId) => gradeId !== gradeConfigId)
        : gradeIds?.concat([gradeConfigId]);
      await formik.setFieldValue(
        `birthdateFilter.filters[${filterIndex}].gradeIds`,
        nextGradeIds
      );
    },
    [formik]
  );
  return (
    <Flex direction="column" width="70%">
      <Text mb={1}>Limit available options based on date of birth</Text>
      <FormLabel
        htmlFor={`dateOfBirthSwitch${questionId}`}
        display="flex"
        flexDirection="row"
        gap="2"
        margin={0}
        mb={1}
      >
        <Switch
          id={`dateOfBirthSwitch${questionId}`}
          isChecked={formik.values.birthdateFilter.type === "on"}
          onChange={async () => {
            const nextValue =
              formik.values.birthdateFilter.type === "on" ? "off" : "on";
            await formik.setFieldValue(`birthdateFilter.type`, nextValue);
            if (nextValue === "on") {
              await formik.setFieldValue(`birthdateFilter.filters`, [
                {
                  type: "BirthdateBeforeFilter",
                  gradeIds: gradesConfig.map((g) => g.id),
                  startDate: today,
                  endDate: today,
                } satisfies Filter,
              ]);
            }
          }}
          isDisabled={disabled}
        />
        <Box>{formik.values.birthdateFilter.type === "on" ? "On" : "Off"}</Box>
      </FormLabel>

      <Text fontSize="sm" fontWeight="400" color="gray.600">
        Will restrict the available grades for parents based on the students
        entered date of birth.
      </Text>

      {formik.values.birthdateFilter.type === "on" ? (
        <Card variant="outline" my={4}>
          {gradesConfig.map((gradeConfig) => {
            const filters = getFilterByGradeId(gradeConfig.id);
            return (
              <GradeConfigurationDisplay
                key={gradeConfig.id}
                gradeConfig={gradeConfig}
                description={
                  filters.length === 0 ? (
                    <Text>Always available</Text>
                  ) : (
                    filters.map((filter: any, index: any) => (
                      <Text key={`${filter.type}${index}`}>
                        When DOB is {getFilterTextAndDates(filter)}
                      </Text>
                    ))
                  )
                }
              />
            );
          })}
          {filteredGradesWithoutGradeConfig.length > 0 &&
            filteredGradesWithoutGradeConfig.map((gradeId) => (
              <GradeConfigurationDisplay
                key={gradeId}
                gradeConfig={{ id: gradeId, label: gradeId }}
                description={<Text>Selected grade is not configured.</Text>}
                isError={true}
              />
            ))}
        </Card>
      ) : null}

      {birthdateFilters.length > 0 ? <Text mb={2}>Limit options</Text> : null}
      <FieldArray name="birthdateFilter.filters">
        {(arrayHelpers) => (
          <>
            {birthdateFilters.map((filter, filterIndex) => (
              <Box key={filterIndex}>
                <Card variant="outline" p={6} my={2}>
                  <Text fontSize="xs" color="gray.800" mb={2}>
                    Selected grades
                  </Text>
                  <Box
                    mb={4}
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      flexDirection: "column",
                    }}
                  >
                    {gradesConfig.map((gradeConfig) => {
                      const isChecked = isFilterChecked(gradeConfig, filter);
                      return (
                        <Checkbox
                          key={gradeConfig.id}
                          isChecked={isChecked}
                          onChange={() =>
                            handleCheckboxChange(
                              isChecked,
                              gradeConfig.id,
                              filterIndex
                            )
                          }
                          isDisabled={disabled}
                          mb={2}
                        >
                          <Text fontSize="xs" fontWeight="400">
                            {gradeConfig.label}
                          </Text>
                        </Checkbox>
                      );
                    })}
                    {filteredGradesWithoutGradeConfig.length > 0 &&
                      filteredGradesWithoutGradeConfig.map(
                        (gradeIdWithoutGradeConfig) => (
                          <Checkbox
                            key={gradeIdWithoutGradeConfig}
                            isChecked={true}
                            onChange={() =>
                              handleCheckboxChange(
                                true,
                                gradeIdWithoutGradeConfig,
                                filterIndex
                              )
                            }
                            mb={2}
                            isDisabled={disabled}
                          >
                            <Text fontSize="xs" fontWeight="400" color="red">
                              {gradeIdWithoutGradeConfig}
                            </Text>
                          </Checkbox>
                        )
                      )}
                  </Box>
                  <SelectControl
                    label="Date of birth range"
                    name={`birthdateFilter.filters[${filterIndex}].type`}
                    isDisabled={disabled}
                  >
                    <option value="BirthdateBeforeFilter">
                      Date of birth before
                    </option>
                    <option value="BirthdateAfterFilter">
                      Date of birth after
                    </option>
                    <option value="BirthdateBetweenFilter">
                      Date of birth between
                    </option>
                  </SelectControl>

                  <Text variant="helperText" mb={4}>
                    Selected grades will be displayed if the students birthdate
                    falls in between or on the selected dates.
                  </Text>

                  <Flex direction="row" gap={2} mb={4}>
                    {filter.type === "BirthdateAfterFilter" ||
                    filter.type === "BirthdateBetweenFilter" ? (
                      <InputControl
                        inputProps={{
                          type: "date",
                          isDisabled: disabled,
                        }}
                        name={`birthdateFilter.filters[${filterIndex}].startDate`}
                        label={
                          filter.type === "BirthdateBetweenFilter"
                            ? "Start date"
                            : "Date"
                        }
                      />
                    ) : null}
                    {filter.type === "BirthdateBeforeFilter" ||
                    filter.type === "BirthdateBetweenFilter" ? (
                      <InputControl
                        inputProps={{
                          type: "date",
                          isDisabled: disabled,
                        }}
                        name={`birthdateFilter.filters[${filterIndex}].endDate`}
                        label={
                          filter.type === "BirthdateBetweenFilter"
                            ? "End date"
                            : "Date"
                        }
                      />
                    ) : null}
                  </Flex>

                  {!disabled && (
                    <Button
                      variant="outline"
                      colorScheme="gray"
                      onClick={async () => {
                        if (birthdateFilters.length === 1) {
                          arrayHelpers.remove(filterIndex);
                          await formik.setFieldValue(
                            "birthdateFilter.type",
                            "off"
                          );
                        } else {
                          arrayHelpers.remove(filterIndex);
                        }
                      }}
                      width="20%"
                    >
                      Remove
                    </Button>
                  )}
                </Card>
              </Box>
            ))}

            {formik.values.birthdateFilter.type === "on" && !disabled ? (
              <>
                <Button
                  colorScheme="gray"
                  alignSelf="flex-start"
                  onClick={() => {
                    arrayHelpers.push({
                      type: "BirthdateBeforeFilter",
                      gradeIds: gradesConfig.map((g) => g.id),
                      startDate: today,
                      endDate: today,
                    } satisfies Filter);
                  }}
                >
                  Add new date range
                </Button>
              </>
            ) : null}
          </>
        )}
      </FieldArray>
    </Flex>
  );
};

function isFilterChecked(gradeConfig: GradeConfig, filter: Filter): boolean {
  return filter.gradeIds.includes(gradeConfig.id);
}

function getFilterTextAndDates(filter: Filter) {
  if (filter.type === "BirthdateBeforeFilter") {
    return `before ${filter.endDate}`;
  }
  if (filter.type === "BirthdateAfterFilter") {
    return `after ${filter.startDate}`;
  }
  if (filter.type === "BirthdateBetweenFilter") {
    return `between ${filter.startDate} and ${filter.endDate}`;
  }
}

export function toFormValue(question: AF.V2.Question): FormikType {
  const filters =
    question.filters
      ?.filter(
        (filter) =>
          filter.type === "BirthdateAfterFilter" ||
          filter.type === "BirthdateBeforeFilter" ||
          filter.type === "BirthdateBetweenFilter"
      )
      .map((filter): Filter => {
        switch (filter.type) {
          case "BirthdateAfterFilter":
            return {
              type: "BirthdateAfterFilter",
              gradeIds: filter.config.keys,
              startDate: filter.config.startDate,
              endDate: undefined,
            };
          case "BirthdateBeforeFilter":
            return {
              type: "BirthdateBeforeFilter",
              gradeIds: filter.config.keys,
              startDate: undefined,
              endDate: filter.config.endDate,
            };
          case "BirthdateBetweenFilter":
            return {
              type: "BirthdateBetweenFilter",
              gradeIds: filter.config.keys,
              startDate: filter.config.startDate,
              endDate: filter.config.endDate,
            };
          default:
            const _exhaustiveCheck: never = filter;
            throw new Error(`Unhandled filter type: ${filter}`);
        }
      }) ?? [];
  const type: "on" | "off" =
    question.type === "Grades" && filters.length > 0 ? "on" : "off";
  const birthdateFilter = { type, filters };
  return { birthdateFilter };
}

type GradeConfigurationDisplayProps = {
  gradeConfig: GradeConfig;
  description: JSX.Element | JSX.Element[];
  isError?: boolean;
};
const GradeConfigurationDisplay = ({
  gradeConfig,
  description,
  isError = false,
}: GradeConfigurationDisplayProps) => (
  <Flex direction="row" key={gradeConfig.id}>
    <Box
      sx={{
        paddingX: 6,
        paddingY: 2,
        fontSize: "xs",
        color: isError ? "red" : "gray.800",
      }}
      width={{ sm: "30%", lg: "20%", xl: "15%" }}
    >
      {gradeConfig.label}
    </Box>
    <Divider orientation="vertical" />
    <Box
      sx={{
        paddingX: 6,
        paddingY: 2,
        fontSize: "xs",
        color: isError ? "red" : "gray.800",
      }}
    >
      {description}
    </Box>
  </Flex>
);
